import type { ListKind } from './types'

/**
 * The list kinds as a picker draws them, in the one order every picker uses:
 * the New list sheet, the list menu's kind change and Add to list's
 * "new list" form. Three files kept their own copy and two of them had
 * drifted — the same choice in two orders teaches a thumb the wrong place.
 */
export const KIND_OPTIONS: { value: ListKind; label: string }[] = [
  { value: 'grocery', label: 'Groceries' },
  { value: 'task', label: 'Tasks' },
  { value: 'buy', label: 'Purchases' },
  { value: 'collection', label: 'Collection' },
  { value: 'template', label: 'Checklist' },
]

/**
 * The Lists index's three sections for an unpinned list: "doing" (Tasks,
 * Checklists), "getting" (Groceries, Purchases), "keeping" (Collections).
 * Here rather than in `Lists.tsx` so Add to list can follow the index's
 * order with the same function instead of a copy of it.
 */
export function listGroupOf(kind: ListKind): 'doing' | 'getting' | 'keeping' {
  if (kind === 'task' || kind === 'template') return 'doing'
  if (kind === 'grocery' || kind === 'buy') return 'getting'
  return 'keeping'
}

/**
 * The kinds an event can go on (UI-audit a2-23, Doug chose: "offer only
 * lists that fit an event"): a Task ("book this"), a Checklist, a
 * Collection. Not Groceries or Purchases — the audit's test wrote a grocery
 * row called "PRIMETIME" from an event sheet.
 */
export const EVENT_LIST_KINDS: readonly ListKind[] = ['task', 'template', 'collection']

/** `KIND_OPTIONS` narrowed to `EVENT_LIST_KINDS`, for Add to list's own
 *  "New list" form: a list started from an event is one that fits one. */
export const EVENT_KIND_OPTIONS = KIND_OPTIONS.filter(o => EVENT_LIST_KINDS.includes(o.value))

/**
 * The lists Add to list offers for an event: unarchived, of a kind that fits
 * one, in the Lists index's own order — Pinned, then Doing, then Keeping
 * ("Getting" is the two kinds that never fit) — and `position` within each,
 * which is the order `useLists` already returns them in. The audit found the
 * sheet in a different order from the index (Tasks, DJs, Groceries… against
 * Tasks, Kindred, Groceries…); the same thumb should find the same list in
 * the same place.
 */
export function listsForEvent<L extends { kind: ListKind; pinned?: boolean | null; archived_at?: string | null }>(
  lists: readonly L[],
): L[] {
  const rank = (l: L) => (l.pinned ? 0 : listGroupOf(l.kind) === 'doing' ? 1 : 2)
  return lists
    .filter(l => !l.archived_at && EVENT_LIST_KINDS.includes(l.kind))
    .map((l, i) => ({ l, i }))
    .sort((a, b) => rank(a.l) - rank(b.l) || a.i - b.i)
    .map(x => x.l)
}

/**
 * Whether a list's kind says anything its name does not (a4-19). Four of
 * Doug's five lists are named for their kind — "Tasks" is a Tasks list — and
 * the desk nav printed "Tasks / Tasks", "Groceries / Groceries". The kind is
 * worth a second line only where it differs: "DJs · Collection", "Kindred
 * prep · Checklist". Case and surrounding space are not a difference.
 */
export const kindAddsToName = (name: string, kindLabel: string): boolean =>
  name.trim().toLocaleLowerCase('en') !== kindLabel.trim().toLocaleLowerCase('en')
